import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ENABLED_UI_LANGUAGES,
  getBaseLanguage,
  setAppLanguage,
  type SupportedLanguage,
} from "@/i18n";
import { apiRequest, queryClient } from "@/lib/queryClient";

type MeResponse = {
  user?: {
    locale?: SupportedLanguage | null;
  };
};

type LanguageSwitcherSize = "sm" | "md";

/** `sm` — kompaktowy trigger (globus + flaga); `md` — kafelek 36px pod pasek akcji sidebara. */
const TRIGGER_CLASS: Record<LanguageSwitcherSize, string> = {
  sm: "flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/[0.06] transition-colors text-white/50 hover:text-white/80",
  md: "relative w-9 h-9 rounded-lg flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.09] transition-colors text-white/45 hover:text-white",
};

type Props = { size?: LanguageSwitcherSize };

export function LanguageSwitcher({ size = "sm" }: Props) {
  const { i18n } = useTranslation();
  const currentLang = getBaseLanguage(i18n.language);

  const currentFlag = ENABLED_UI_LANGUAGES.find((l) => l.code === currentLang)?.flag ?? "🇵🇱";

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    await setAppLanguage(lang);
    queryClient.setQueryData<MeResponse>(["/api/auth/me"], (previous) => {
      if (!previous?.user) return previous;
      return { ...previous, user: { ...previous.user, locale: lang } };
    });

    // Best-effort: persist to backend so AI prompts/errors/currency follow.
    // Failure is non-fatal — i18next still applies the change locally.
    try {
      await apiRequest("PATCH", "/api/auth/me", { locale: lang });
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    } catch {
      // ignore — anonymous user or network blip
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={TRIGGER_CLASS[size]}>
          {size === "md" ? (
            <>
              <Globe size={18} />
              <span className="absolute -bottom-0.5 -right-0.5 text-[10px] leading-none">
                {currentFlag}
              </span>
            </>
          ) : (
            <>
              <Globe className="w-3.5 h-3.5" />
              <span className="text-xs">{currentFlag}</span>
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0a0a0f] border-white/10 min-w-[160px]">
        {ENABLED_UI_LANGUAGES.map(({ code, label, flag }) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleLanguageChange(code)}
            className={`flex items-center gap-2 cursor-pointer text-sm ${
              currentLang === code ? "text-[#ef3e29] font-medium" : "text-white/70"
            }`}
          >
            <span>{flag}</span>
            <span>{label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
